import { InputForm, type InputFormRef } from "./InputForm"
import { MessageItem } from "./MessageItem"
import CloseIcon from "@/assets/icons/close.svg"
import { SESSION_ID_KEY } from "@/constants"
import { useChatHistory } from "@/hooks/useChatHistory"
import { useChatOpen } from "@/hooks/useChatOpen"
import { useChatSocket } from "@/hooks/useChatSocket"
import { useDateObserver } from "@/hooks/useDateObserver"
import { useExternalChatEvents } from "@/hooks/useExternalChatEvents"
import { formatDate } from "@/utils/formatDates"
import { useCallback, useEffect, useRef, useState } from "react"
import { v4 as uuidv4 } from "uuid"

export function Chat() {
  // === STATE ===
  const [sessionId] = useState(() => {
    const saved = localStorage.getItem(SESSION_ID_KEY)
    if (saved) return saved
    const id = uuidv4()
    localStorage.setItem(SESSION_ID_KEY, id)
    return id
  })

  // === REFS ===
  const messagesContainer = useRef<HTMLDivElement | null>(null)
  const inputFormRef = useRef<InputFormRef>(null)

  // === HELPERS ===
  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      if (messagesContainer.current) messagesContainer.current.scrollTop = messagesContainer.current.scrollHeight
    }, 5)
  }, [])

  const { visibleDate, refreshObserver } = useDateObserver(messagesContainer)

  // === CHAT OPEN/CLOSE ===
  const { openChat, closeChat, isOpen, inputField } = useChatOpen({
    scrollToBottom,
    refreshObserver,
  })

  // === HISTORY ===
  const { messages, setMessages, loadingHistory } = useChatHistory(sessionId)

  // === INIT ===
  useChatSocket({ setMessages, setSending: useCallback((val: boolean) => inputFormRef.current?.setSending(val), []), scrollToBottom })

  const { comagicId } = useExternalChatEvents({ openChat, closeChat })

  // === OPEN/CLOSE ANIMATION ===
  const [showChat, setShowChat] = useState(false)
  const [animateOpen, setAnimateOpen] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setShowChat(true)
      requestAnimationFrame(() => setAnimateOpen(true))
    } else {
      setAnimateOpen(false)
      const timeout = setTimeout(() => setShowChat(false), 300)
      return () => clearTimeout(timeout)
    }
  }, [isOpen])

  if (!showChat) return null

  return (
    <div
      id="chat-bot-fsk"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Escape" && closeChat()}
      className={`${chatWrapperClasses} transition-all duration-300 ease-in-out transform ${
        animateOpen ? "opacity-100 scale-100" : "opacity-0 scale-95"
      }`}
    >
      <button
        onClick={closeChat}
        className="absolute top-0 right-0 w-12 h-12 flex justify-center items-center bg-[#3d3c3c99] rounded-tr-xl rounded-bl-xl"
      >
        <CloseIcon className="w-5 h-5 fill-[#ffffff]" />
      </button>

      <div ref={messagesContainer} className="flex-1 p-4 overflow-y-auto bg-[#141414] rounded-[inherit] max-sm:rounded-b-2xl flex flex-col gap-4">
        {loadingHistory && (
          <div className="flex justify-center items-center py-4 text-[#ffffff] text-sm max-lg:text-xs">⏳ Загрузка сообщений...</div>
        )}

        {visibleDate && (
          <div className="!sticky top-0 z-[1] w-fit !mx-auto py-2 px-4 rounded-xl bg-[#14141480] text-[#ffffff] text-sm max-lg:text-xs">
            {formatDate(visibleDate)}
          </div>
        )}

        {messages.map((message, idx) => (
          <MessageItem key={message.id} message={message} setMessages={setMessages} idx={idx} sessionId={sessionId || ""} />
        ))}

        {messages.length === 0 && <div className="flex justify-center items-center py-4 text-[#ffffff] text-sm">Нет сообщений</div>}
      </div>

      <InputForm
        ref={inputFormRef}
        inputField={inputField}
        scrollToBottom={scrollToBottom}
        refreshObserver={refreshObserver}
        setMessages={setMessages}
        sessionId={sessionId}
        comagicId={comagicId}
      />
    </div>
  )
}

const chatWrapperClasses = [
  "fixed bottom-4 right-4 p-5 bg-[#1f1f1f] rounded-2xl shadow-2xl overflow-hidden flex flex-col gap-5",
  "w-[512px] max-h-[calc(100svh-2rem)] h-[872px]",
  "max-lg:w-[360px] max-lg:h-[507px]",
  "max-sm:w-[100svw] max-sm:max-h-[calc(100svh-3rem)] max-sm:h-full max-sm:bottom-0 max-sm:right-0 max-sm:rounded-b-none",
].join(" ")
